import React, { JSX, useMemo } from 'react';
import { Review } from '@box-types';
import { getInitials } from '@box/utils';
import dayjs from 'dayjs';
import { translate } from '@box-core/services';
import { Rating } from '../rating/rating.component';

type ReviewProps = {
  review: Review;
};

const Review: React.FC<ReviewProps> = ({ review }) => {
  const getReviewFormattedDate = (review: Review): string => {
    return dayjs(review.createdStamp).format('DD/MM/YY');
  };

  const getReviewInitials = (review: Review): string => {
    const firstName = review.firstName ?? '';
    const lastName = review.lastName ?? '';
    return getInitials(`${firstName} ${lastName}`);
  };

  const getReviewRecommendation = (review: Review): string => {
    if (!review.products?.length) return '';
    return review.products.map((v) => v.name).join(', ');
  };

  const getReviewName = (review: Review): string => {
    const firstName = review.firstName ?? '';
    const lastName = review.lastName ?? '';
    const lastNameInitial = getInitials(lastName);
    return `${firstName} ${lastNameInitial}`;
  };

  // useMemo is used to avoid recalculating derived values unless shop changes
  const date = useMemo(() => getReviewFormattedDate(review), [review]);
  const name = useMemo(() => getReviewName(review), [review]);
  const initials = useMemo(() => getReviewInitials(review), [review]);
  const recommendation = useMemo(() => getReviewRecommendation(review), [review]);

  const AvatarFrameIcon: JSX.Element = (
    <svg width="36" height="34" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink">
      <defs>
        <path
          d="M17 0c9.205 0 16.667 6.625 16.667 14.796 0 8.172-7.462 14.797-16.667 14.797-2.981 0-5.78-.695-8.2-1.912a3.112 3.112 0 0 1-.28.271c-2.51 1.919-5.208 2.248-7.087 1.954-.624-.098-.756-.952-.187-1.232 1.72-.855 2.801-2.55 3.432-3.922-2.7-2.623-4.345-6.119-4.345-9.956C.333 6.625 7.795 0 17 0Z"
          id="a"
        />
      </defs>
      <g transform="translate(1 2)" fill="none" fillRule="evenodd">
        <mask id="b" fill="#fff">
          <use xlinkHref="#a" />
        </mask>
        <g mask="url(#b)">
          <path d="M-3-4h40v40H-3z" fill="#09CC94" fillRule="nonzero" />
        </g>
      </g>
    </svg>
  );

  return (
    <div className="review-component">
      <div className="review-component-avatar">
        <div className="review-component-avatar-frame">{AvatarFrameIcon}</div>
        <span className="review-component-avatar-initials ods-typography-body-s-bold">{initials}</span>
      </div>

      <div className="review-component-text">
        <p className="review-component-text-name ods-typography-body-m-bold">{name}.</p>
        {recommendation && (
          <p className="review-component-text-recommendation ods-typography-body-s-regular">
            {translate('i_suggest')} {recommendation}
          </p>
        )}
      </div>

      <div className="review-component-rating ods-typography-microcopy-regular">
        <div className="review-component-rating-value">
          <span className="review-component-rating-value-number">{review.rating.toFixed(1)}</span>
          <Rating disabled={true} rating={review.rating} />
        </div>
        <p className="review-component-rating-date">{date}</p>
      </div>
    </div>
  );
};

export default React.memo(Review);
